import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react'

import { Crumbs } from '../../components/Crumbs'
import '../../components/kit/back.css'
import { GROUPS, entryAddress, entryScreen, type Entry } from './entries'

// The one frame every entry of Settings opens in.
//
// # WHAT IT PROMISES
//
// Settings never leaves the screen. Its groups and the entries of the open
// group are on the page for every entry, the one that is open is marked, and
// any other entry is one press away in its own group and two in another. It was
// not so before 11 Oct 2026: six entries were screens of their own, and opening
// one replaced the whole page with it, four of the six with no way back.
//
// # WHY IT IS A BAR ABOVE THE CONTENT (12 Oct 2026)
//
// For a day the entries were a list down the left of the content, which stood
// them beside the console's own side navigation. The founder's words on seeing
// it: two levels of left navigation is not really looking good. So the five
// groups run across the top as tabs and the open group's entries are a row
// under them. The content has the full width at every window size, which is
// also why the frame no longer measures itself: the list form needed a second,
// narrow form for entries too wide to stand beside it, a rule to choose between
// the two and a picker for the narrow one. None of that is needed by a bar.
//
// The first strip of chips Settings had (before the list) held all seventeen
// entries in one run, wrapped to two lines at 1366 px and started the pane at
// y240. This is not that: one row of five group names, and one row of at most
// eight entries.
//
// # A GROUP OPENS ITS FIRST ENTRY
//
// Pressing a group shows its entries and opens the first of them. For a few
// hours it showed the entries and opened nothing, which left the content of
// another group on the screen under the one just pressed (the founder, 12 Oct
// 2026: the first sub item should load into the content area). Pressing the
// group the open entry is already in changes nothing. Every entry of every
// group is in the document as a link (the groups not shown are hidden), so the
// address of each is there to read whichever group is showing.
//
// # THE WAY BACK (13 Oct 2026, plan 3.9)
//
// Above the bar is the shared breadcrumb, "Settings / <entry>" (Back model B1
// and B2: where the page sits, the page itself last and not a link). Its first
// crumb opens the index #/settings now lands on (SettingsIndex.tsx). It
// replaces the title link "Settings", which opened the first entry held.
//
// # FOCUS
//
// Choosing an entry moves focus to the heading of what opened, once it is on the
// page (a screen draws its heading after it has loaded), unless the person has
// already moved on. So does arriving from the index, whose link left the page
// with it. The bar sits between the page's skip link and the entry's
// first control, so the frame has a skip link of its own that lands on the
// entry's heading.

const groupId = (g: string) => 'settings-group-' + g.toLowerCase().replace(/\W+/g, '-')

// Set by the index as it opens an entry, so the frame that opens puts the
// focus on the entry's heading; a page loaded at an entry's address is left
// as the browser has it.
export const fromIndex = { current: false }

export function SettingsFrame({ shown, current, children }: { shown: Entry[]; current: Entry | undefined; children: ReactNode }) {
  const root = useRef<HTMLDivElement>(null)
  const content = useRef<HTMLDivElement>(null)

  // The group whose entries are showing: the open entry's, until another tab is
  // pressed, and the open entry's again whenever the entry changes.
  const [looking, setLooking] = useState<string | null>(null)
  useEffect(() => {
    setLooking(null)
  }, [current?.key])
  const showing = looking ?? current?.group ?? GROUPS[0]

  const chose = useRef(false)
  const choose = () => {
    chose.current = true
    // Choosing the entry already open changes nothing, and a later Back must
    // not be taken for a choice.
    window.setTimeout(() => {
      chose.current = false
    }, 1500)
  }
  useEffect(() => {
    if (!chose.current) return
    chose.current = false
    return headingFocus(content.current, root.current)
  }, [current?.key])
  // Opened from the index: the link pressed is gone, and the focus with it.
  useEffect(() => {
    if (!fromIndex.current) return
    fromIndex.current = false
    return headingFocus(content.current, root.current)
  }, [])

  // Past the bar, to what is open.
  const skip = () => {
    const box = content.current
    const target = box?.querySelector<HTMLElement>('h1') ?? box
    if (!target) return
    target.tabIndex = -1
    target.focus()
  }

  const groups = GROUPS.filter((g) => shown.some((e) => e.group === g))

  return (
    <div className="p1-page settings-frame" ref={root}>
      <button type="button" className="skip-link" onClick={skip}>
        Skip to {current?.label ?? 'the setting'}
      </button>
      <Crumbs className="kit-crumbs settings-crumbs" home={{ label: 'Settings', href: '#/settings' }} current={current?.label ?? 'Settings'} />
      <nav className="settings-nav" aria-label="Settings" onClick={(ev: MouseEvent) => (ev.target as Element).closest('a') && choose()}>
        <div className="settings-nav-heads">
          {groups.map((g) => (
            <button
              key={g}
              type="button"
              className="settings-nav-head"
              id={groupId(g)}
              aria-expanded={showing === g}
              aria-controls={groupId(g) + '-entries'}
              // The group the open entry is in keeps its mark while another is looked at.
              data-open={current?.group === g ? '' : undefined}
              onClick={() => {
                setLooking(g)
                const first = shown.find((e) => e.group === g)
                if (first && current?.group !== g) {
                  choose()
                  window.location.hash = entryAddress(first).replace(/^#/, '')
                }
              }}
            >
              {g}
            </button>
          ))}
        </div>
        {groups.map((g) => (
          // A labelled group, as the sidebar's own sections are, and not a
          // heading: the page has one h1, and a screen drawn in the content has
          // its own headings below it.
          <div key={g} className="settings-nav-group" role="group" aria-labelledby={groupId(g)} id={groupId(g) + '-entries'} hidden={showing !== g}>
            <ul role="list">
              {shown
                .filter((e) => e.group === g)
                .map((e) => (
                  <li key={e.key}>
                    <a href={entryAddress(e)} data-nav-id={'tprm.' + entryScreen(e)} aria-current={current?.key === e.key ? 'page' : undefined} className="settings-nav-item">
                      {e.label}
                    </a>
                  </li>
                ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="settings-content" ref={content}>
        {children}
      </div>
    </div>
  )
}

// Moves focus to the heading of what opened in the frame, as soon as there is
// one, and gives up if the person has gone elsewhere in the meantime: focus
// taken from somebody who has started typing in the new screen is worse than
// focus left on the bar. Returns what undoes the wait.
export function headingFocus(content: HTMLElement | null, frame: HTMLElement | null): () => void {
  if (!content || !frame) return () => {}
  const mine = () => {
    const a = document.activeElement
    return !a || a === document.body || (frame.contains(a) && !content.contains(a))
  }
  const focusIt = () => {
    const h = content.querySelector<HTMLElement>('h1')
    if (!h) return false
    if (mine()) {
      h.tabIndex = -1
      h.focus()
    }
    return true
  }
  if (focusIt()) return () => {}
  const watch = new MutationObserver(() => {
    if (focusIt()) watch.disconnect()
  })
  watch.observe(content, { childList: true, subtree: true })
  const stop = window.setTimeout(() => watch.disconnect(), 5000)
  return () => {
    watch.disconnect()
    window.clearTimeout(stop)
  }
}
